import React, { useState } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFetch, PageHeader, Loading, GenericCrud } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRightLeft, Plus } from "lucide-react";

function Movements() {
  const { data: moves, loading, refetch } = useFetch("/inventory/movements", []);
  const { data: items } = useFetch("/inventory/items", []);
  const { data: players } = useFetch("/players?sort_by=name_asc", []);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "OUT", quantity: 1 });

  const submit = async () => {
    try { await client.post("/inventory/movements", f); toast.success("Stock updated"); setOpen(false); setF({ type: "OUT", quantity: 1 }); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setOpen(true)} data-testid="movement-add"><ArrowRightLeft size={16} className="me-1" /> New Movement</Button>
      </div>
      {loading ? <Loading /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border"><tr>
                {["Date", "Item", "Size", "Qty", "Type", "Player", "Reason", "By"].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-border">
                {(moves || []).length === 0 && <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No movements yet</td></tr>}
                {(moves || []).map((m) => (
                  <tr key={m.id} className="hover:bg-muted/40" data-testid={`movement-${m.id}`}>
                    <td className="px-4 py-3">{(m.date || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-medium">{m.item_name}</td>
                    <td className="px-4 py-3">{m.size || "—"}</td>
                    <td className="px-4 py-3">{m.quantity}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${m.type === "IN" ? "bg-emerald-500/15 text-emerald-700" : "bg-rose-500/15 text-rose-700"}`}>{m.type}</span></td>
                    <td className="px-4 py-3">{m.player_name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{m.reason || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{m.admin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Stock Movement</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="mb-1.5 block">Item</Label>
              <Select value={f.item_id || ""} onValueChange={(v) => setF({ ...f, item_id: v })}>
                <SelectTrigger data-testid="movement-item"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>{(items || []).map((i) => <SelectItem key={i.id} value={i.id}>{i.name} {i.size ? `(${i.size})` : ""} — stock {i.quantity}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="mb-1.5 block">Type</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                  <SelectTrigger data-testid="movement-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="OUT">OUT (issue)</SelectItem><SelectItem value="IN">IN (restock)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block">Quantity</Label><Input type="number" min={1} value={f.quantity} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) })} data-testid="movement-qty" /></div>
            </div>
            <div><Label className="mb-1.5 block">Player (optional)</Label>
              <Select value={f.player_id || ""} onValueChange={(v) => setF({ ...f, player_id: v })}>
                <SelectTrigger data-testid="movement-player"><SelectValue placeholder="Select player" /></SelectTrigger>
                <SelectContent>{(players || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Reason</Label><Input value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="movement-reason" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submit} data-testid="movement-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Inventory() {
  return (
    <div>
      <PageHeader title="Inventory & Stock" subtitle="Equipment items and stock movements" testid="inventory-page" />
      <Tabs defaultValue="items">
        <TabsList><TabsTrigger value="items" data-testid="tab-items">Items</TabsTrigger><TabsTrigger value="movements" data-testid="tab-movements">Movements</TabsTrigger></TabsList>
        <TabsContent value="items" className="mt-4">
          <GenericCrud
            title="" endpoint="/inventory/items" testid="items"
            columns={[
              { key: "name", label: "Item", render: (r) => <span className="font-semibold">{r.name}</span> },
              { key: "category", label: "Category" },
              { key: "size", label: "Size" },
              { key: "quantity", label: "In Stock", render: (r) => <span className={r.quantity <= (r.min_quantity || 0) ? "text-rose-600 font-bold" : "font-semibold"}>{r.quantity}</span> },
              { key: "min_quantity", label: "Min" },
            ]}
            fields={[
              { key: "name", label: "Item Name" },
              { key: "category", label: "Category", type: "select", options: ["Jersey", "Shorts", "Socks", "Tracksuit", "Ball", "Cones", "Bibs", "Training", "Other"].map((c) => ({ value: c, label: c })) },
              { key: "size", label: "Size" },
              { key: "quantity", label: "Quantity", type: "number" },
              { key: "min_quantity", label: "Minimum Alert", type: "number" },
            ]}
          />
        </TabsContent>
        <TabsContent value="movements" className="mt-4"><Movements /></TabsContent>
      </Tabs>
    </div>
  );
}
